/**
 * Generate jigsaw piece clip-paths from SVG die-line text.
 * Browser-compatible port of genPieces.js (no fs/path dependencies).
 *
 * @param {string} svgText — raw SVG file content
 * @param {number} grid — grid size (e.g. 6, 9, or 12)
 * @returns {Array<{ row: number, col: number, index: number, path: string }>}
 */

const SVG_SIZE = 1536;

function splitByM(d) {
    return d.trim().split(/(?=M\s)/).map(s => s.trim()).filter(Boolean);
}

function parse(d) {
    const cmds = [];
    const t = d.match(/[MCLZAHVmclzahv]|[-+]?[0-9]*\.?[0-9]+/g);
    if (!t) return cmds;
    let i = 0, cur = null;
    while (i < t.length) {
        if (/^[MCLZAHVmclzahv]$/.test(t[i])) {
            cur = t[i].toUpperCase(); i++;
            if (cur === "Z") { cmds.push({ type: "Z" }); continue; }
        }
        if (cur === "M") { cmds.push({ type: "M", x: +t[i], y: +t[i + 1] }); i += 2; cur = "C"; }
        else if (cur === "C") { cmds.push({ type: "C", x1: +t[i], y1: +t[i + 1], x2: +t[i + 2], y2: +t[i + 3], x3: +t[i + 4], y3: +t[i + 5] }); i += 6; }
        else if (cur === "L") { cmds.push({ type: "L", x: +t[i], y: +t[i + 1] }); i += 2; }
        else if (cur === "A") { i += 7; }
        else { i++; }
    }
    return cmds;
}

function splitIntoCells(segD, grid) {
    const cmds = parse(segD);
    const cells = [];
    let idx = 0;
    if (cmds[idx]?.type === "M") idx++;
    for (let c = 0; c < grid; c++) {
        const cc = [];
        for (let k = 0; k < 3; k++) {
            if (idx < cmds.length && cmds[idx].type === "C") { cc.push(cmds[idx]); idx++; }
        }
        cells.push(cc);
    }
    return cells;
}

function toStr(cmds) {
    return cmds.map(c =>
        c.type === "C" ? `C ${c.x1} ${c.y1} ${c.x2} ${c.y2} ${c.x3} ${c.y3}` :
        c.type === "L" ? `L ${c.x} ${c.y}` : ""
    ).join(" ");
}

function reverse(cmds, sx, sy) {
    const r = [];
    for (let i = cmds.length - 1; i >= 0; i--) {
        const c = cmds[i];
        if (c.type === "C") {
            const prev = i > 0 ? cmds[i - 1] : null;
            r.push({ type: "C", x1: c.x2, y1: c.y2, x2: c.x1, y2: c.y1, x3: prev ? prev.x3 : sx, y3: prev ? prev.y3 : sy });
        }
    }
    return r;
}

export default function generatePiecePaths(svgText, grid) {
    const CELL = SVG_SIZE / grid;
    const allPaths = [];
    const re = /d="([^"]+)"/g;
    let m;
    while ((m = re.exec(svgText)) !== null) allPaths.push(m[1]);
    if (allPaths.length < 2) {
        throw new Error("SVG must have at least 2 path elements (horizontal + vertical die lines)");
    }

    const hCells = splitByM(allPaths[0]).map(s => splitIntoCells(s, grid));
    const vCells = splitByM(allPaths[1]).map(s => splitIntoCells(s, grid));

    const pieces = [];
    for (let row = 0; row < grid; row++) {
        for (let col = 0; col < grid; col++) {
            const x0 = col * CELL, y0 = row * CELL;
            const x1 = (col + 1) * CELL, y1 = (row + 1) * CELL;
            let d = `M ${x0} ${y0}`;

            // Top edge
            if (row === 0) d += ` L ${x1} ${y0}`;
            else d += " " + toStr(hCells[row - 1][col]);

            // Right edge
            if (col === grid - 1) d += ` L ${x1} ${y1}`;
            else d += " " + toStr(vCells[col][row]);

            // Bottom edge (reversed)
            if (row === grid - 1) d += ` L ${x0} ${y1}`;
            else {
                const bc = hCells[row][col];
                const bs = col > 0 ? hCells[row][col - 1] : null;
                d += " " + toStr(reverse(bc, bs ? bs[bs.length - 1].x3 : 0, bs ? bs[bs.length - 1].y3 : y1));
            }

            // Left edge (reversed)
            if (col === 0) d += ` L ${x0} ${y0}`;
            else {
                const lc = vCells[col - 1][row];
                const ls = row > 0 ? vCells[col - 1][row - 1] : null;
                d += " " + toStr(reverse(lc, ls ? ls[ls.length - 1].x3 : x0, ls ? ls[ls.length - 1].y3 : 0));
            }

            d += " Z";
            pieces.push({ row, col, index: row * grid + col, path: d });
        }
    }
    return pieces;
}
